// src/components/modals/UploadFootageModal.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as bootstrap from 'bootstrap';
import API from '../../config/config'; // Make sure to import the config
import { useAuth } from '../../context/AuthContext';
import LoadingModal from './LoadingModal';

const UploadFootageModal = ({ eventId, show, handleClose }) => {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [fileSizeError, setFileSizeError] = useState('');
  const MAX_TOTAL_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
  const MAX_FILES = 100;

  const modalRef = useRef(null);
  const modalInstance = useRef(null);
  const fileInputRef = useRef(null);

  // Debug the eventId
  useEffect(() => {
  }, [eventId]);

  // Modal initialization
  useEffect(() => {
    if (modalRef.current) {
      // Initialize Bootstrap Modal
      modalInstance.current = new bootstrap.Modal(modalRef.current, {
        backdrop: 'static', // Prevent closing by clicking outside
        keyboard: false,    // Prevent closing with Esc key
      });

      // Event listener for when the modal is hidden
      const handleHidden = () => {
        resetState();
        handleClose(); // Notify parent to update the state
      };
      modalRef.current.addEventListener('hidden.bs.modal', handleHidden);

      // Cleanup event listener on unmount
      return () => {
        if (modalInstance.current) {
          modalInstance.current.dispose();
        }
        if (modalRef.current) {
          modalRef.current.removeEventListener('hidden.bs.modal', handleHidden);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Show/hide modal
  useEffect(() => {
    if (modalInstance.current) {
      if (show) {
        modalInstance.current.show();
      } else {
        modalInstance.current.hide();
      }
    }
  }, [show]);

  // Modal event listeners
  useEffect(() => {
    const handleHidden = () => {
      resetState();
      handleClose();
    };

    const modalElement = modalRef.current;
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', handleHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
      }
    };
  }, [handleClose]);

  const resetState = () => {
    setFiles([]);
    setLoading(false);
    setMessage({ type: '', text: '' });
    setUploadProgress(0);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFileSizeError('');
    
    // Check file count
    if (selectedFiles.length > MAX_FILES) {
      setFileSizeError(`Maximum ${MAX_FILES} files allowed at once.`);
      return;
    }
    
    // Calculate total file size
    const totalSize = selectedFiles.reduce((total, file) => total + file.size, 0);
    setTotalFileSize(totalSize);
    
    // Check total size
    if (totalSize > MAX_TOTAL_SIZE) {
      const sizeInGB = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
      setFileSizeError(`Total file size (${sizeInGB}GB) exceeds the maximum limit of 5GB. Please select fewer or smaller files.`);
      return;
    }
    
    // Check individual file sizes
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_TOTAL_SIZE);
    if (oversizedFiles.length > 0) {
      setFileSizeError(`Some files are larger than 5GB. Please compress them or select different files.`);
      return;
    }
    
    setFiles(selectedFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Modal event listeners
  useEffect(() => {
    const handleHidden = () => {
      resetState();
      handleClose();
    };

    const modalElement = modalRef.current;
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', handleHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
      }
    };
  }, [handleClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (fileSizeError) {
      setMessage({ type: 'danger', text: fileSizeError });
      return;
    }
    
    if (!files || files.length === 0) {
      setMessage({ type: 'danger', text: 'Please select files to upload' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      
      // Use 'files' as the field name to match backend Multer config
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axios.post(
        `${API.BASE_URL}/api/footage/upload/${eventId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${currentUser.token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
          timeout: 300000 // 5 minutes timeout for large uploads
        }
      );
      
      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: `Successfully uploaded ${files.length} files!` });
        
        // Dispatch event to notify parent component
        const uploadEvent = new CustomEvent('footageUploaded', { 
          detail: { eventId: eventId } 
        });
        document.dispatchEvent(uploadEvent);
        
        setTimeout(() => {
          if (modalInstance.current) {
            modalInstance.current.hide();
          }
        }, 2000);
      } else {
        setMessage({ type: 'danger', text: response.data?.message || 'Failed to upload files' });
      }
    } catch (error) {
      console.error('Error uploading footage:', error);
      
      if (error.code === 'ECONNABORTED') {
        setMessage({ type: 'danger', text: 'Upload timeout. Please try with fewer or smaller files.' });
      } else if (error.response?.status === 413) {
        setMessage({ type: 'danger', text: 'Files too large. Maximum total size is 5GB.' });
      } else {
        setMessage({ type: 'danger', text: error.response?.data?.message || 'Upload failed. Please try again.' });
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div
      className="modal fade"
      id="uploadFootageModal"
      tabIndex="-1"
      aria-labelledby="uploadFootageLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog modal-lg modal-dialog-top">
        <div className="modal-content custom-modal bg-dark text-white">
          <div className="modal-header">
            <h5 className="modal-title" id="uploadFootageLabel">
              Upload Event Footage
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3" align='start'>
                <label className="form-label">Upload Photos/Videos</label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  required
                  onChange={handleFileChange}
                />
                
                {/* File size information */}
                {files.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Selected: {files.length} files ({formatFileSize(totalFileSize)})
                    </small>
                  </div>
                )}
                
                {/* File size error */}
                {fileSizeError && (
                  <div className="alert alert-warning mt-2" role="alert">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    {fileSizeError}
                  </div>
                )}
                
                {/* Upload limits info */}
                <small className="text-muted d-block mt-1">
                  Maximum: {MAX_FILES} files, 5GB total size
                </small>
              </div>
              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn text-white bg_red rounded-2 border-0"
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload Photos'}
                </button>
              </div>
            </form>

            {/* Show upload progress if uploading */}
            {message.text && (
              <div className="mt-3" align='center'>
                {message.type === 'success' && (
                  <div className="alert alert-success" role="alert">
                    {message.text}
                  </div>
                )}
                {message.type === 'danger' && (
                  <div className="alert alert-danger" role="alert">
                    {message.text}
                  </div>
                )}
                {message.type === 'warning' && (
                  <div className="alert alert-warning" role="alert">
                    {message.text}
                  </div>
                )}
                {uploadProgress > 0 && (
                  <div>
                    <div className="progress">
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {uploadProgress}%
                      </div>
                    </div>
                    <p className="mt-2">{message.text}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

UploadFootageModal.propTypes = {
  eventId: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default UploadFootageModal;
